/**
 * Browser storage that cannot throw, with a one-time read of the old key name.
 *
 * Every caller used to hand-roll the same try/catch — storage throws outright
 * in a private window and when a browser is set to block site data, and an
 * uncaught throw there takes the whole render with it.
 *
 * The keys were all `crm.`-prefixed. localStorage is already scoped to the
 * origin and this origin serves one app, so the prefix said nothing — but the
 * values are real: a seller's saved table views, their queue position, their
 * focus sort. `legacyKey` lets a rename read the old name once and write the
 * new one, so nobody loses anything on deploy day.
 */

type Store = 'local' | 'session'

function backing(store: Store): Storage | null {
  try {
    return store === 'local' ? localStorage : sessionStorage
  } catch {
    return null
  }
}

export function readStored(key: string, opts: { store?: Store; legacyKey?: string } = {}): string | null {
  const s = backing(opts.store ?? 'local')
  if (!s) return null
  try {
    const current = s.getItem(key)
    if (current !== null) return current
    if (!opts.legacyKey) return null
    const legacy = s.getItem(opts.legacyKey)
    if (legacy === null) return null
    // Carry it across on first read so the old name stops being consulted.
    try {
      s.setItem(key, legacy)
      s.removeItem(opts.legacyKey)
    } catch { /* full or read-only — the value still gets returned */ }
    return legacy
  } catch {
    return null
  }
}

export function writeStored(key: string, value: string, opts: { store?: Store } = {}): void {
  const s = backing(opts.store ?? 'local')
  if (!s) return
  try {
    s.setItem(key, value)
  } catch {
    /* private window or quota — losing a preference is not worth throwing over */
  }
}

export function clearStored(key: string, opts: { store?: Store } = {}): void {
  const s = backing(opts.store ?? 'local')
  if (!s) return
  try {
    s.removeItem(key)
  } catch { /* nothing to do */ }
}
